/**
 * Auto Campaign Builder
 * Fully automated campaign creation with minimal user input
 * Uses Google Ads API v22 Recommendations + AI
 * 
 * INTEGRATION:
 * - Now checks for existing CustomerIntelligence before fresh analysis
 * - Uses campaignContextProvider for unified context
 * - Falls back to fresh analysis if no CI exists
 */

import { googleAdsService } from './googleAdsService';
import { googleAdsApiClient } from './googleAdsApiClient';
import { keywordStrategyService } from './keywordStrategyService';
import { openaiService } from './openaiService';
import { CampaignDraft, KeywordIdea, LandingPageAnalysis, BrandProfile } from '../types/googleAds';
import { websiteCrawler } from './websiteCrawler';
import { conversionIntelligence } from './conversionIntelligence';
import { imageHarvester, StoredAsset } from './imageHarvester';
import { logoDetector } from './logoDetector';
import { assetRepository } from './assetRepository';
import { IndustryTemplate } from '../types/industryTemplate';
import { templateRequirementsChecker } from './templateRequirementsChecker';
import { industryTemplateService } from './industryTemplateService';
import { customerIntelligenceService } from './customerIntelligenceService';
import { campaignContextProvider, CampaignCreationContext } from './campaignContextProvider';
import { getClient } from './geminiService';
import { truncateHeadline, truncateDescription } from '../utils/googleAdsHelpers';

export interface AutoCampaignResult {
  draft: CampaignDraft;
  forecast?: {
    expectedClicks: number;
    expectedImpressions: number;
    expectedCost: number;
    expectedConversions: number;
  };
  explanation: string;
  aiDecisions: {
    keywords: string;
    budget: string;
    bidding: string;
    adGroups: string;
  };
  harvestedAssets?: {
    images: StoredAsset[];
    logos: StoredAsset[];
    totalImages: number;
  };
  conversionTracking?: any;
  websiteInsights?: any;
}

export const autoCampaignBuilder = {
  /**
   * Fully automated campaign creation
   * User provides only Landing Page URL
   * 
   * FLOW:
   * 1. Check for existing CustomerIntelligence (reuse if available)
   * 2. If not found, do fresh analysis
   * 3. Check for industry template
   * 4. Build campaign with context
   */
  async buildCampaign(
    orgId: string,
    customerId: string | undefined,
    landingPageUrl: string,
    options?: {
      targetBudget?: number;
      isDemo?: boolean;
      userId?: string;
      forceRefresh?: boolean;
    }
  ): Promise<AutoCampaignResult> {
    console.log('🤖 Auto Mode: Building campaign automatically...');

    try {
      // STEP 0: Check for existing CustomerIntelligence
      console.log('🔍 Step 0/11: Checking for existing Customer Intelligence...');
      let analysis: LandingPageAnalysis;
      let ciContext: CampaignCreationContext | null = null;
      
      if (!options?.forceRefresh) {
        try {
          const hasCI = await campaignContextProvider.hasContext(orgId, landingPageUrl);
          if (hasCI) {
            console.log('✅ Found existing Customer Intelligence, using cached data...');
            ciContext = await campaignContextProvider.getCampaignContext(
              orgId,
              landingPageUrl,
              options?.userId || 'system',
              {
                includeKeywords: true,
                includeAdSuggestions: true,
                createIfNotFound: false
              }
            );
            
            // Build analysis from CI context
            analysis = this.buildAnalysisFromContext(ciContext);
            console.log(`📊 Using CI data for: ${ciContext.businessName}`);
          } else {
            console.log('ℹ️ No existing CI found, performing fresh analysis...');
            analysis = await googleAdsService.analyzeLandingPage(landingPageUrl);
          }
        } catch (e) {
          console.warn('⚠️ CI lookup failed, falling back to fresh analysis:', e);
          analysis = await googleAdsService.analyzeLandingPage(landingPageUrl);
        }
      } else {
        // Force fresh analysis
        console.log('📊 Step 1/11: Analyzing landing page (forced refresh)...');
        analysis = await googleAdsService.analyzeLandingPage(landingPageUrl);
      }

      // STEP 1.5: Check for industry template (NEW)
      console.log('🎯 Step 1.5/11: Checking for industry template...');
      const detectedIndustry = analysis.industry?.toLowerCase() || '';
      const template = await industryTemplateService.getTemplateForIndustry(detectedIndustry);
      
      if (template) {
        console.log(`✅ Found template: ${template.name}`);
        console.log('🔄 Switching to template-based campaign builder...');
        return this.buildCampaignFromTemplate(analysis, template, orgId, options);
      }
      
      console.log('ℹ️ No template found, using generic builder');
      // Continue with original flow below

      // STEP 2: Deep Website Crawling (NEW)
      console.log('🕷️ Step 2/11: Crawling website structure...');
      let websiteInsights = null;
      try {
        websiteInsights = await websiteCrawler.crawlWebsite(landingPageUrl);
        console.log(`✅ Found ${websiteInsights.conversionPages.length} conversion pages`);
      } catch (crawlError) {
        console.warn('⚠️ Website crawling failed:', crawlError);
      }

      // STEP 3: Conversion Tracking Detection (NEW)
      console.log('🎯 Step 3/11: Detecting conversion tracking...');
      let conversionTracking = null;
      try {
        conversionTracking = await conversionIntelligence.analyzeConversionTracking(landingPageUrl);
        console.log(`✅ Platform: ${conversionTracking.platform}, Events: ${conversionTracking.events.length}`);
      } catch (trackingError) {
        console.warn('⚠️ Conversion tracking detection failed:', trackingError);
      }

      // STEP 4: Image Harvesting (NEW)
      console.log('📸 Step 4/11: Harvesting images from website...');
      let harvestedImages: StoredAsset[] = [];
      try {
        harvestedImages = await imageHarvester.harvestAndStoreImages(
          landingPageUrl,
          orgId,
          (msg) => console.log(msg)
        );
        
        // Add to asset repository
        if (harvestedImages.length > 0) {
          await assetRepository.addHarvestedImages(orgId, harvestedImages);
        }
        
        console.log(`✅ Harvested ${harvestedImages.length} images`);
      } catch (harvestError) {
        console.warn('⚠️ Image harvesting failed:', harvestError);
      }

      // STEP 5: Logo Detection (NEW)
      console.log('🎨 Step 5/11: Extracting logo...');
      let logos: StoredAsset[] = [];
      try {
        logos = await logoDetector.extractLogo(landingPageUrl);
        console.log(`✅ Found ${logos.length} logos`);
      } catch (logoError) {
        console.warn('⚠️ Logo detection failed:', logoError);
      }

      let keywordIdeas: KeywordIdea[] = [];
      let adGroupThemes: any[] = [];
      let recommendations: any = null;

      // STEP 6-8: Google APIs (only if connected)
      if (!options?.isDemo && customerId) {
        try {
          // STEP 6: Keyword Ideas (Google Keyword Planner)
          console.log('🔍 Step 6/11: Expanding keywords with Google Keyword Planner...');
          // Use detected language from analysis, fallback to German (DACH default)
          const targetLocation = analysis.businessInfo?.country === 'United States' ? 'United States' : 'Germany';
          const targetLanguage = analysis.detectedLanguageName || 'German';
          
          keywordIdeas = await googleAdsApiClient.generateKeywordIdeas(
            orgId,
            customerId,
            analysis.suggestedKeywords.slice(0, 5),
            landingPageUrl,
            [targetLocation],
            targetLanguage
          );

          // STEP 7: Ad Group Themes (Google)
          console.log('📁 Step 7/11: Generating ad group themes...');
          const topKeywords = keywordIdeas
            .sort((a, b) => b.monthlySearches - a.monthlySearches)
            .slice(0, 20)
            .map(k => k.text);
          
          adGroupThemes = await googleAdsApiClient.generateAdGroupThemes(
            orgId,
            customerId,
            topKeywords.length > 0 ? topKeywords : analysis.suggestedKeywords,
            landingPageUrl
          );

          // STEP 8: Recommendations (Google)
          console.log('💡 Step 8/11: Getting Google recommendations...');
          recommendations = await googleAdsApiClient.generateRecommendations(
            orgId,
            customerId,
            {
              advertisingChannelType: 'SEARCH',
              seedKeywords: analysis.suggestedKeywords
            }
          );
        } catch (apiError) {
          console.warn('⚠️ Google API error, using fallback data:', apiError);
          // Continue with mock data
        }
      }

      // Fallback: Generate real keywords with Gemini from scraped content
      if (keywordIdeas.length === 0) {
        console.log('📝 Generating REAL keywords with Gemini from scraped content...');
        keywordIdeas = await keywordStrategyService.generateKeywordsWithGemini(analysis, 20);
      }

      // LANGUAGE CONSISTENCY: Ensure keywords match campaign language
      const language = this.getConsistentLanguage(analysis);
      console.log(`🌐 Campaign Language: ${language.languageName} (${language.languageCode})`);
      
      // Filter keywords for language consistency (especially important for German campaigns)
      if (language.isGerman && keywordIdeas.length > 0) {
        const originalCount = keywordIdeas.length;
        // Keep keywords that contain German characters or are likely German
        keywordIdeas = keywordIdeas.filter(kw => {
          const text = kw.text.toLowerCase();
          // Keep if contains umlauts (ä, ö, ü, ß) or common German patterns
          return text.includes('ä') || text.includes('ö') || text.includes('ü') || text.includes('ß') ||
                 text.includes('kaufen') || text.includes('online') || text.includes('service') ||
                 // Or if it doesn't contain common English-only words
                 !(text.includes('buy') && !text.includes('ü')) &&
                 !(text.includes('get') && !text.includes('ä'));
        });
        console.log(`✅ Language filter: ${keywordIdeas.length}/${originalCount} keywords match ${language.languageName}`);
      }

      // STEP 5: AI Relevance Scoring & Filtering
      console.log('🎯 Step 9/11: Scoring keywords with AI...');
      const scoredKeywords = await this.scoreAndSelectKeywords(
        keywordIdeas,
        analysis,
        15 // Top 15
      );

      // STEP 6: AI Match Type Strategy
      console.log('🎭 Step 10/11: Applying match type strategy...');
      const keywordsWithMatchTypes = scoredKeywords.map(keyword => {
        const strategy = keywordStrategyService.recommendMatchType(keyword, analysis);
        return {
          ...keyword,
          recommendedMatchType: strategy.matchType,
          matchTypeReason: strategy.reason,
          matchTypeConfidence: strategy.confidence
        };
      });

      // STEP 7: AI Ad Copy Generation with Gemini (Language-consistent, API v22 compliant)
      console.log('✍️ Step 11/11: Generating ad copy variations with Gemini...');
      const adCopy1 = await this.generateAdCopyWithGemini(analysis, 'search', { headlines: 10, descriptions: 4 });
      const adCopy2 = await this.generateAdCopyWithGemini(analysis, 'search', { headlines: 10, descriptions: 4 }, 'urgent');

      // STEP 8: Budget Calculation
      const suggestedBudget = this.calculateOptimalBudget(
        keywordsWithMatchTypes,
        recommendations,
        options?.targetBudget
      );

      // STEP 9: Bidding Strategy
      const biddingStrategy = recommendations?.recommendations?.find((r: any) => 
        r.maximize_conversions_opt_in_recommendation
      ) ? 'maximize_conversions' : 'maximize_conversions';

      // Assemble Campaign Draft
      const draft: CampaignDraft = {
        orgId,
        createdBy: 'auto',
        landingPageUrl,
        analysis,
        campaignType: 'search',
        campaignName: `${analysis.title} - Auto Search Campaign`,
        dailyBudget: suggestedBudget,
        locations: ['Germany'], // DACH-Fokus: Deutschland als Default (statt United States)
        languages: ['German'],  // DACH-Fokus: Deutsch als Default (statt English)
        startDate: new Date(),
        biddingStrategy: biddingStrategy as any,
        searchSettings: {
          keywords: keywordsWithMatchTypes.map(k => ({
            text: k.text,
            matchType: (k.recommendedMatchType?.toLowerCase() || 'phrase') as any
          })),
          negativeKeywords: [],
          headlines: [
            ...adCopy1.headlines.slice(0, 5),
            ...adCopy2.headlines.slice(0, 5)
          ],
          descriptions: [
            ...adCopy1.descriptions.slice(0, 2),
            ...adCopy2.descriptions.slice(0, 2)
          ],
          adGroups: adGroupThemes.length > 0 ? adGroupThemes.map((theme: any, idx: number) => ({
            id: `ag${idx + 1}`,
            name: theme.suggested_ad_group_name || `Ad Group ${idx + 1}`,
            keywords: (theme.suggested_keywords || []).map((kw: string) => ({
              text: kw,
              matchType: 'phrase'
            }))
          })) : [{
            id: 'ag1',
            name: 'Ad Group 1',
            keywords: keywordsWithMatchTypes.slice(0, 10).map(k => ({
              text: k.text,
              matchType: k.recommendedMatchType?.toLowerCase() || 'phrase'
            }))
          }],
          displayUrlPath: new URL(landingPageUrl).pathname
        }
      };

      // Mock forecast for demo
      const forecast = {
        expectedClicks: Math.round(suggestedBudget * 30 / 2), // Rough estimate
        expectedImpressions: Math.round(suggestedBudget * 30 / 2 * 50),
        expectedCost: suggestedBudget * 30,
        expectedConversions: Math.round(suggestedBudget * 30 / 2 * 0.03)
      };

      const aiDecisions = {
        keywords: `Selected top ${keywordsWithMatchTypes.length} keywords by relevance and search volume. ${
          keywordsWithMatchTypes.filter(k => k.recommendedMatchType === 'EXACT').length
        } EXACT for high-intent, ${
          keywordsWithMatchTypes.filter(k => k.recommendedMatchType === 'PHRASE').length
        } PHRASE for balanced reach, ${
          keywordsWithMatchTypes.filter(k => k.recommendedMatchType === 'BROAD').length
        } BROAD for discovery.`,
        budget: `Google ${recommendations ? 'recommended' : 'estimated'} ${suggestedBudget} €/Tag based on keyword competition and expected traffic.`,
        bidding: `Maximize Conversions strategy ${recommendations ? 'recommended by Google' : 'selected'} for optimal ROI without manual bid management.`,
        adGroups: adGroupThemes.length > 0 
          ? `Google suggested ${adGroupThemes.length} ad groups based on keyword themes for better organization.`
          : `Single ad group created for simplified management.`
      };

      console.log('✅ Auto campaign built successfully!');

      return {
        draft,
        forecast,
        explanation: `AI analyzed your landing page and Google Ads data to create an optimized campaign. All settings are based on industry best practices and your specific business.`,
        aiDecisions,
        harvestedAssets: {
          images: harvestedImages,
          logos: logos,
          totalImages: harvestedImages.length + logos.length
        },
        conversionTracking,
        websiteInsights
      };

    } catch (error) {
      console.error('Error building auto campaign:', error);
      throw error;
    }
  },

  /**
   * Score and select top keywords
   */
  async scoreAndSelectKeywords(
    keywordIdeas: KeywordIdea[],
    analysis: LandingPageAnalysis,
    limit: number
  ): Promise<KeywordIdea[]> {
    // Score each keyword with brand detection
    const scored = await Promise.all(
      keywordIdeas.map(async (keyword) => {
        const brandDetection = keywordStrategyService.detectBrandKeyword(keyword.text, analysis);
        const relevanceScore = await keywordStrategyService.calculateRelevanceScore(
          keyword,
          analysis
        );
        
        // PENALTY for branded keywords in auto mode (-30 points)
        const brandPenalty = brandDetection.isBranded ? 30 : 0;
        const adjustedRelevance = Math.max(0, relevanceScore - brandPenalty);
        
        return { 
          ...keyword, 
          relevanceScore: adjustedRelevance,
          isBranded: brandDetection.isBranded,
          brandMatchType: brandDetection.matchType
        };
      })
    );

    // Sort by composite score: relevance + search volume + (inverse) competition
    const sorted = scored.sort((a, b) => {
      const scoreA = (a.relevanceScore || 0) * 0.5 + 
                     Math.log(a.monthlySearches + 1) * 0.3 +
                     (100 - a.competitionIndex) * 0.2;
      const scoreB = (b.relevanceScore || 0) * 0.5 + 
                     Math.log(b.monthlySearches + 1) * 0.3 +
                     (100 - b.competitionIndex) * 0.2;
      return scoreB - scoreA;
    });

    // Separate branded and non-branded
    const nonBranded = sorted.filter(k => !k.isBranded);
    const branded = sorted.filter(k => k.isBranded).slice(0, 2);  // Max 2 branded keywords for defense
    
    // Prioritize non-branded, add few branded for defense
    const selected = [...nonBranded.slice(0, limit - 2), ...branded];
    
    const brandedSelected = selected.filter(k => k.isBranded).length;
    console.log(`🎯 Auto-selected: ${selected.length - brandedSelected} non-branded, ${brandedSelected} branded (for brand defense)`);
    
    return selected.slice(0, limit);
  },

  /**
   * Calculate optimal budget
   */
  calculateOptimalBudget(
    keywords: KeywordIdea[],
    recommendations: any,
    targetBudget?: number
  ): number {
    // If user specified target, use that
    if (targetBudget && targetBudget > 0) {
      return targetBudget;
    }

    // If Google recommended, use that
    if (recommendations?.recommendations) {
      const budgetRec = recommendations.recommendations.find((r: any) => 
        r.campaign_budget_recommendation
      );
      if (budgetRec?.campaign_budget_recommendation?.recommended_budget_amount_micros) {
        return Math.round(
          budgetRec.campaign_budget_recommendation.recommended_budget_amount_micros / 1000000
        );
      }
    }

    // Calculate based on keyword bids
    const avgBid = keywords.reduce((sum, k) => sum + k.suggestedBid, 0) / keywords.length;
    const estimatedClicks = 30; // Conservative estimate
    const suggestedBudget = Math.ceil(avgBid * estimatedClicks);

    // Clamp between 20 € and 200 €
    return Math.max(20, Math.min(200, suggestedBudget));
  },

  /**
   * Get consistent language from analysis with German as fallback
   * Ensures language consistency across entire campaign (keywords, ad copy, location)
   */
  getConsistentLanguage(analysis: any): { 
    languageName: string; 
    languageCode: string;
    isGerman: boolean;
  } {
    const detectedLang = analysis.detectedLanguageName || 
                         analysis.language ||
                         'German'; // Default: German for DACH focus
    
    const isGerman = detectedLang === 'German' || 
                     detectedLang.toLowerCase() === 'deutsch' ||
                     detectedLang === 'de';
    
    return {
      languageName: isGerman ? 'German' : detectedLang,
      languageCode: isGerman ? 'de' : (detectedLang.toLowerCase().substring(0, 2)),
      isGerman
    };
  },

  /**
   * Validate ad copy against Google Ads API v22 policies
   * Returns validation result with reason if invalid
   */
  validateAPIv22(text: string): { valid: boolean; reason?: string } {
    const trimmed = text.trim();
    
    // 1. Must not be empty
    if (trimmed.length === 0) {
      return { valid: false, reason: 'Empty content' };
    }
    
    // 2. No excessive capitalization (max 20% CAPS for text > 10 chars)
    if (trimmed.length > 10) {
      const capsCount = (trimmed.match(/[A-Z]/g) || []).length;
      const capsRatio = capsCount / trimmed.length;
      if (capsRatio > 0.2) {
        return { valid: false, reason: 'Excessive capitalization (API v22 violation)' };
      }
    }
    
    // 3. No repeated punctuation
    if (trimmed.includes('!!') || trimmed.includes('??') || trimmed.includes('...')) {
      return { valid: false, reason: 'Repeated punctuation (API v22 violation)' };
    }
    
    // 4. No emojis or special unicode
    const emojiRegex = /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/u;
    if (emojiRegex.test(trimmed)) {
      return { valid: false, reason: 'Contains emojis (API v22 violation)' };
    }
    
    return { valid: true };
  },

  /**
   * Clean and validate ad copy array for API v22 compliance
   * Removes invalid entries, duplicates, and ensures all pass validation
   */
  cleanAPIv22(texts: string[]): string[] {
    const cleaned = texts
      .map(t => {
        let text = t.trim();
        
        // ══════════════════════════════════════════════════════════════
        // STEP 1: Remove ALL technical formatting (slugs, underscores)
        // ══════════════════════════════════════════════════════════════
        
        // Replace underscores with spaces
        text = text.replace(/_/g, ' ');
        
        // Replace hyphens used as word separators (but keep valid hyphens like "24-Stunden")
        text = text.replace(/([a-z])-([a-z])/gi, '$1 $2');
        
        // Fix double spaces
        text = text.replace(/\s+/g, ' ').trim();
        
        // ══════════════════════════════════════════════════════════════
        // STEP 2: Title case for all-lowercase slugs
        // ══════════════════════════════════════════════════════════════
        if (text === text.toLowerCase() && text.includes(' ')) {
          text = text.split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
        }
        
        // ══════════════════════════════════════════════════════════════
        // STEP 3: REJECT texts that still look like slugs or have issues
        // ══════════════════════════════════════════════════════════════
        
        // Reject if still contains underscores (shouldn't happen but safety check)
        if (text.includes('_')) {
          console.warn(`❌ Rejected (underscore): "${text}"`);
          return '';
        }
        
        // Reject if it looks like a slug pattern (multiple lowercase-dash-lowercase)
        if (/^[a-z]+-[a-z]+-[a-z]+/.test(text)) {
          console.warn(`❌ Rejected (slug pattern): "${text}"`);
          return '';
        }
        
        // Reject if text is truncated with "..." (incomplete generation)
        if (text.endsWith('...') || text.endsWith('…')) {
          console.warn(`❌ Rejected (truncated): "${text}"`);
          return '';
        }
        
        return text;
      })
      .filter(t => t.length > 0)
      .filter((t, index, self) => self.indexOf(t) === index) // Remove duplicates
      .filter(t => this.validateAPIv22(t).valid); // Only valid entries
    
    return cleaned;
  },

  /**
   * Build LandingPageAnalysis from CampaignCreationContext
   * Used when CustomerIntelligence data is available
   */
  buildAnalysisFromContext(context: CampaignCreationContext): LandingPageAnalysis {
    console.log('🔄 Building analysis from CustomerIntelligence context...');
    
    return {
      url: context.url,
      title: context.businessName,
      valueProposition: context.brand.usp,
      targetAudience: context.brand.targetAudience,
      keyBenefits: context.brand.keyBenefits,
      features: context.brand.offerings,
      callToAction: context.brand.primaryCTA,
      industry: context.industry,
      suggestedKeywords: [...context.keywords.core, ...context.keywords.longTail].slice(0, 20),
      tone: context.voice.formality,
      recommendedCampaignType: 'search',
      
      // Business context from CI
      businessContext: {
        businessModel: context.businessModel,
        usp: context.brand.usp,
        slogan: context.brand.slogan,
        offerings: context.brand.offerings,
        industryConfidence: context.confidence
      },
      
      // Brand identity from CI
      brandIdentity: {
        tone: context.voice.formality,
        toneEvidence: context.voice.toneAttributes.join(', '),
        addressForm: context.voice.addressForm,
        colors: context.visual.brandColors,
        primaryColor: context.visual.primaryColor
      },
      
      // Language
      detectedLanguage: context.language,
      detectedLanguageName: context.languageName,
      
      // Images
      logoUrl: context.visual.logoUrl,
      imageUrls: context.visual.brandImages,
      
      // Keywords
      industryKeywords: context.keywords.industrySpecific,
      
      // Image analysis
      imageAnalysis: {
        visualStyle: context.visual.visualStyle as any,
        suggestedTypes: context.visual.imageStyle?.subjects || []
      },
      
      // Tonality from voice
      tonality: {
        formality: context.voice.formality,
        addressForm: context.voice.addressForm,
        evidence: context.voice.examplePhrases.join('. ')
      },
      
      // Recommended budget (estimate based on industry)
      recommendedBudget: {
        min: 20,
        max: 100,
        recommended: 50
      }
    };
  },

  /**
   * Enrich landing page analysis with persistent Brand Profile data
   * This ensures brand consistency across all campaigns
   */
  enrichAnalysisWithBrandProfile(
    analysis: LandingPageAnalysis,
    brandProfile: BrandProfile
  ): LandingPageAnalysis {
    console.log('🏢 Enriching analysis with Brand Profile...');
    
    return {
      ...analysis,
      // Override with brand profile data where it's more reliable
      industry: brandProfile.industry || analysis.industry,
      
      // Merge business context
      businessContext: {
        businessModel: brandProfile.businessModel || analysis.businessContext?.businessModel || 'unknown',
        usp: brandProfile.usp || analysis.businessContext?.usp || analysis.valueProposition || '',
        slogan: brandProfile.slogan || analysis.businessContext?.slogan,
        offerings: brandProfile.offerings.length > 0 
          ? brandProfile.offerings 
          : analysis.businessContext?.offerings || [],
        competitors: analysis.businessContext?.competitors,
        industryConfidence: brandProfile.industryConfidence || analysis.businessContext?.industryConfidence || 80,
        industryReasoning: analysis.businessContext?.industryReasoning || '',
        alternativeIndustries: analysis.businessContext?.alternativeIndustries
      },
      
      // Merge brand identity
      brandIdentity: {
        tone: brandProfile.tone || analysis.brandIdentity?.tone || 'formal',
        toneEvidence: brandProfile.toneEvidence || analysis.brandIdentity?.toneEvidence || '',
        addressForm: brandProfile.addressForm || analysis.brandIdentity?.addressForm || 'Sie',
        colors: brandProfile.brandColors.length > 0 
          ? brandProfile.brandColors 
          : analysis.brandIdentity?.colors || [],
        primaryColor: brandProfile.primaryColor || analysis.brandIdentity?.primaryColor,
        brandImages: brandProfile.brandImages.length > 0 
          ? brandProfile.brandImages 
          : analysis.brandIdentity?.brandImages || []
      },
      
      // Merge competitor context (brand profile has priority for consistent messaging)
      competitorContext: {
        mentionedCompetitors: analysis.competitorContext?.mentionedCompetitors || [],
        marketPosition: analysis.competitorContext?.marketPosition || '',
        differentiators: brandProfile.differentiators.length > 0 
          ? brandProfile.differentiators 
          : analysis.competitorContext?.differentiators || [],
        avoidPhrases: brandProfile.avoidPhrases.length > 0 
          ? brandProfile.avoidPhrases 
          : analysis.competitorContext?.avoidPhrases || [],
        uniqueAdAngles: brandProfile.uniqueAdAngles.length > 0 
          ? brandProfile.uniqueAdAngles 
          : analysis.competitorContext?.uniqueAdAngles || [],
        competitiveAdvantages: brandProfile.competitiveAdvantages.length > 0 
          ? brandProfile.competitiveAdvantages 
          : analysis.competitorContext?.competitiveAdvantages || []
      },
      
      // Use brand profile keywords as base, merge with page-specific
      suggestedKeywords: [
        ...brandProfile.coreKeywords,
        ...(analysis.suggestedKeywords || []).filter(kw => 
          !brandProfile.coreKeywords.includes(kw)
        )
      ].slice(0, 15),
      
      // Use brand profile benefits
      keyBenefits: brandProfile.keyBenefits.length > 0 
        ? brandProfile.keyBenefits 
        : analysis.keyBenefits || [],
        
      // Use brand profile CTA
      callToAction: brandProfile.primaryCTA || analysis.callToAction || 'Kontakt aufnehmen',
      
      // Language from brand profile
      detectedLanguage: brandProfile.primaryLanguage || analysis.detectedLanguage,
      detectedLanguageName: brandProfile.primaryLanguageName || analysis.detectedLanguageName
    };
  },

  /**
   * Generate ad copy using Gemini with Google Ads API v22 compliance
   * Replacement for OpenAI - no temperature restrictions, better German support
   * Ensures language consistency across entire campaign
   * 
   * @param brandProfile Optional persistent brand profile for consistent messaging
   */
  async generateAdCopyWithGemini(
    analysis: any,
    campaignType: 'search' | 'pmax',
    count: { headlines: number; descriptions: number },
    tone?: string,
    brandProfile?: BrandProfile
  ): Promise<{ headlines: string[]; descriptions: string[]; longHeadlines?: string[] }> {
    
    // If brand profile provided, enrich analysis first
    const enrichedAnalysis = brandProfile 
      ? this.enrichAnalysisWithBrandProfile(analysis, brandProfile)
      : analysis;
    try {
      const client = getClient();
      
      // ══════════════════════════════════════════════════════════════════════
      // CRITICAL: Clean ALL inputs BEFORE using in prompt to prevent slug leakage
      // ══════════════════════════════════════════════════════════════════════
      const cleanInput = (text: string | undefined | null): string => {
        if (!text || typeof text !== 'string') return '';
        return text
          .replace(/_/g, ' ')                    // Replace underscores
          .replace(/([a-z])-([a-z])/gi, '$1 $2') // Replace word-hyphens
          .replace(/\s+/g, ' ')                  // Normalize spaces
          .trim()
          .split(' ')
          .map(word => {
            // Title case for all-lowercase words, preserve proper casing
            if (word === word.toLowerCase() && word.length > 2) {
              return word.charAt(0).toUpperCase() + word.slice(1);
            }
            return word;
          })
          .join(' ');
      };
      
      // Get consistent language (use enriched analysis)
      const lang = this.getConsistentLanguage(enrichedAnalysis);
      
      const languageInstruction = lang.isGerman
        ? `🇩🇪 ABSOLUT KRITISCH: Generiere ALLE Texte NUR auf DEUTSCH!
           Verwende deutsche Wörter: "Jetzt", "Kostenlos", "Mehr erfahren", "Angebot", "Kontakt"
           NIEMALS Englisch: NICHT "Get", "Free", "Learn more", "Offer", "Contact"`
        : `Generate ALL content in ${lang.languageName} ONLY! No mixing languages!`;
      
      const toneDesc = tone === 'urgent' ? 'urgent, action-oriented' : (enrichedAnalysis.tone || 'professional');
      
      // Extract and CLEAN all real data from enriched analysis (includes brand profile if available)
      const businessName = cleanInput(enrichedAnalysis.businessName || enrichedAnalysis.title?.split(' ')[0]) || 'Unternehmen';
      const industry = cleanInput(enrichedAnalysis.industry) || 'Dienstleistungen';
      const keywords = (enrichedAnalysis.suggestedKeywords || []).slice(0, 5).map(cleanInput).filter(Boolean).join(', ');
      const benefits = (enrichedAnalysis.keyBenefits || []).slice(0, 5).map(cleanInput).filter(Boolean).join(', ');
      
      // Use businessContext fields (enriched with brand profile if available)
      const usp = cleanInput(enrichedAnalysis.businessContext?.usp || enrichedAnalysis.valueProposition) || '';
      const offerings = (enrichedAnalysis.businessContext?.offerings || []).slice(0, 5).map(cleanInput).filter(Boolean).join(', ');
      const businessModel = enrichedAnalysis.businessContext?.businessModel || 'unknown';
      
      // Use brandIdentity fields (enriched with brand profile if available)
      const brandTone = enrichedAnalysis.brandIdentity?.tone || enrichedAnalysis.tonality?.formality || 'formal';
      const addressForm = enrichedAnalysis.brandIdentity?.addressForm || enrichedAnalysis.tonality?.addressForm || 'Sie';
      
      // Determine target audience based on business model
      const targetAudienceLabel = businessModel === 'b2b' 
        ? 'Geschäftskunden und Unternehmen' 
        : businessModel === 'b2c' 
          ? 'Privatkunden und Verbraucher'
          : cleanInput(enrichedAnalysis.targetAudience) || 'Interessenten';
      
      // Extract competitor context for differentiation (enriched with brand profile if available)
      const competitorContext = enrichedAnalysis.competitorContext;
      const differentiators = (competitorContext?.differentiators || []).map(cleanInput).filter(Boolean).join(', ');
      const uniqueAdAngles = (competitorContext?.uniqueAdAngles || []).map(cleanInput).filter(Boolean).join(', ');
      const avoidPhrases = (competitorContext?.avoidPhrases || []).map(cleanInput).filter(Boolean).join(', ');
      const competitiveAdvantages = (competitorContext?.competitiveAdvantages || []).map(cleanInput).filter(Boolean).join(', ');
      
      // Extract image analysis for visual context
      const imageAnalysis = enrichedAnalysis.imageAnalysis;
      const detectedProducts = (imageAnalysis?.detectedProducts || []).map(cleanInput).filter(Boolean).join(', ');
      const detectedServices = (imageAnalysis?.detectedServices || []).map(cleanInput).filter(Boolean).join(', ');
      const visualStyle = imageAnalysis?.visualStyle || 'professional';
      
      const apiV22Requirements = `
GOOGLE ADS API v22 REQUIREMENTS (ABSOLUTELY CRITICAL):
1. Headlines: MUST be ≤30 characters (count EVERY character including spaces!)
2. Descriptions: MUST be ≤90 characters (count EVERY character including spaces!)
3. NO excessive capitalization (max 20% uppercase letters)
4. NO repeated punctuation (no !!, ??, etc.)
5. NO emojis or special unicode characters
6. NO leading or trailing spaces
7. Each line must be unique (no duplicates)
8. Professional, policy-compliant language only

⚠️ ABSOLUT VERBOTEN - Diese Fehler NIEMALS machen:
- ❌ KEINE Unterstriche: "AI_consulting" ist FALSCH!
- ❌ KEINE technischen Slugs: "ai-solutions-and-consulting" ist FALSCH!
- ❌ KEINE CamelCase: "AiConsulting" ist FALSCH!
- ❌ KEINE Branchennamen in Headlines (zu generisch!)
- ✅ Stattdessen: Konkrete Benefits, Firmennamen, Handlungsaufrufe

GUTE vs. SCHLECHTE HEADLINES:
❌ SCHLECHT: "Professional AI Consulting" (generisch, langweilig)
❌ SCHLECHT: "Best ai_solutions_and..." (technischer Slug!)
❌ SCHLECHT: "Quality AI Services" (nichtssagend)
✅ GUT: "${businessName} - Ihr Partner" (Firmenname + Nutzen)
✅ GUT: "Jetzt Beratung anfragen" (klarer CTA)
✅ GUT: "Bis zu 30% Ersparnis" (konkreter Benefit)
✅ GUT: "Kostenlose Erstberatung" (Angebot)`;
      
      const prompt = campaignType === 'search'
        ? `Du bist ein EXPERTE für Google Ads Copywriting mit 10+ Jahren Erfahrung. 
Erstelle HOCHKONVERTIERENDE Headlines und Descriptions für eine Google Search Kampagne.

${languageInstruction}

═══════════════════════════════════════════════════════════════
📊 GESCHÄFTSDATEN (bereits bereinigt - VERWENDEN WIE ANGEGEBEN):
═══════════════════════════════════════════════════════════════
- Firmenname: ${businessName}
- Branche: ${industry}
- USP: ${usp || 'Professioneller Service'}
- Angebote/Services: ${offerings || 'Beratung und Dienstleistungen'}
- Zielgruppe: ${targetAudienceLabel}
- Geschäftsmodell: ${businessModel === 'b2b' ? 'B2B (Geschäftskunden)' : businessModel === 'b2c' ? 'B2C (Privatkunden)' : 'Gemischt'}
- Sprache: ${lang.languageName}
- Tonalität: ${brandTone === 'formal' ? 'Formell (Sie-Form)' : 'Locker (Du-Form)'}
- Ansprache: ${addressForm}-Form
- Keywords: ${keywords || 'Branchenspezifisch'}
- Benefits: ${benefits || 'Qualität, Service, Expertise'}
- Call-to-Action: ${cleanInput(analysis.callToAction) || 'Kontakt aufnehmen'}

${differentiators || uniqueAdAngles || competitiveAdvantages ? `
═══════════════════════════════════════════════════════════════
🏆 WETTBEWERBS-DIFFERENZIERUNG (NUTZE DIESE!):
═══════════════════════════════════════════════════════════════
${differentiators ? `- DIFFERENZIERUNGSMERKMALE: ${differentiators}` : ''}
${uniqueAdAngles ? `- EINZIGARTIGE AD-WINKEL: ${uniqueAdAngles}` : ''}
${competitiveAdvantages ? `- WETTBEWERBSVORTEILE: ${competitiveAdvantages}` : ''}
${avoidPhrases ? `
⚠️ VERMEIDE diese generischen Phrasen (nutzt jeder Wettbewerber!):
${avoidPhrases}` : ''}
` : ''}

${detectedProducts || detectedServices ? `
═══════════════════════════════════════════════════════════════
🖼️ AUS BILDERN ERKANNT (Vision AI):
═══════════════════════════════════════════════════════════════
${detectedProducts ? `- PRODUKTE: ${detectedProducts}` : ''}
${detectedServices ? `- SERVICES: ${detectedServices}` : ''}
- VISUELLER STIL: ${visualStyle}
→ Nutze diese konkreten Produkte/Services in Headlines!
` : ''}

═══════════════════════════════════════════════════════════════
🎯 HEADLINE-STRATEGIE (${count.headlines} Headlines, MAX 30 Zeichen):
═══════════════════════════════════════════════════════════════

WICHTIG: Generiere EINZIGARTIGE, KONKRETE Headlines - KEINE generischen Phrasen!

1. 🏢 BRAND HEADLINES (2x): Nur den Firmennamen verwenden
   Beispiele: "${businessName}", "${businessName} GmbH"

2. 🎁 BENEFIT HEADLINES (3x): KONKRETE Vorteile mit Zahlen/Fakten
   ${lang.isGerman 
     ? 'Beispiele: "Bis zu 30% sparen", "In 24h geliefert", "10+ Jahre Erfahrung"'
     : 'Examples: "Save Up to 30%", "Delivered in 24h", "10+ Years Experience"'}

3. 📞 CTA HEADLINES (3x): Klare Handlungsaufforderungen
   ${lang.isGerman 
     ? 'Beispiele: "Jetzt Angebot holen", "Kostenlos beraten", "Termin buchen"'
     : 'Examples: "Get Quote Now", "Free Consultation", "Book Now"'}

4. ⭐ USP HEADLINES (2x): Basierend auf dem USP
   ${usp ? `Inspiriert von: "${usp}"` : 'Einzigartige Stärken hervorheben'}

═══════════════════════════════════════════════════════════════
📝 DESCRIPTION-STRATEGIE (${count.descriptions} Descriptions, MAX 90 Zeichen):
═══════════════════════════════════════════════════════════════

Jede Description MUSS enthalten:
- ✅ Einen KONKRETEN Nutzen (nicht generisch!)
- ✅ ${addressForm}-Form für Ansprache
- ✅ Einen Call-to-Action am Ende

${lang.isGerman 
  ? `Beispiel: "Professionelle ${offerings.split(',')[0] || 'Beratung'} für ${businessModel === 'b2b' ? 'Ihr Unternehmen' : 'Sie'}. ${cleanInput(analysis.callToAction) || 'Jetzt anfragen'}!"`
  : `Example: "Professional ${offerings.split(',')[0] || 'services'} for your needs. ${cleanInput(analysis.callToAction) || 'Contact us'}!"`}

${apiV22Requirements}

Return ONLY JSON (KEINE Unterstriche, KEINE Slugs, NATÜRLICHE Sprache):
{
  "headlines": ["headline1", "headline2", ...],
  "descriptions": ["desc1", "desc2", ...]
}`
        : `Du bist ein EXPERTE für Google Ads Performance Max Kampagnen.
Erstelle HOCHKONVERTIERENDE Assets für maximale Performance über alle Google-Kanäle.

${languageInstruction}

═══════════════════════════════════════════════════════════════
📊 ECHTE WEBSITE-DATEN (aus Firecrawl Scraping):
═══════════════════════════════════════════════════════════════
- Firmenname: ${businessName}
- Branche: ${analysis.industry}
- USP: ${usp}
- Zielgruppe: ${analysis.targetAudience || 'Interessenten'}
- Sprache: ${lang.languageName}
- Tonalität: ${analysis.tonality?.addressForm || 'Sie'}-Form (${analysis.tonality?.formality || 'formal'})
- Echte Keywords: ${keywords}
- Echte Benefits: ${benefits}
- Call-to-Action: ${analysis.callToAction || 'Kontakt'}

═══════════════════════════════════════════════════════════════
🎯 ASSET-STRATEGIE FÜR PMAX:
═══════════════════════════════════════════════════════════════

📌 KURZE HEADLINES (${count.headlines}x, MAX 30 Zeichen):
- Mix aus Brand, Benefit, CTA Headlines
- Für Search, Display, YouTube, Gmail

📌 LANGE HEADLINES (5x, MAX 90 Zeichen):
- Vollständige Value Propositions
- Für Display und Discovery Anzeigen
${lang.isGerman 
  ? `- Beispiel: "Entdecken Sie ${businessName} - ${usp || 'Ihr Partner für professionelle Lösungen'}"`
  : `- Example: "Discover ${businessName} - ${usp || 'Your partner for professional solutions'}"`}

📌 DESCRIPTIONS (${count.descriptions}x, MAX 90 Zeichen):
- Benefit + Trust + CTA Struktur
- Verschiedene Varianten für A/B Testing

═══════════════════════════════════════════════════════════════
⚠️ QUALITÄTSKONTROLLE:
═══════════════════════════════════════════════════════════════
${lang.isGerman 
  ? `- KRITISCH: ${analysis.tonality?.addressForm || 'Sie'}-Form konsequent verwenden!
- Deutsche Wörter: "Jetzt", "Kostenlos", "Professionell", "Qualität", "Entdecken"
- NIEMALS Englisch mischen!`
  : '- Professional English, engaging tone for broad audience'}

${apiV22Requirements}

Return ONLY JSON:
{
  "headlines": ["headline1", "headline2", ...],
  "longHeadlines": ["long1", "long2", "long3", "long4", "long5"],
  "descriptions": ["desc1", "desc2", ...]
}`;

      console.log(`🤖 Generating ${lang.languageName} ad copy with Gemini (API v22 compliant)...`);

      const response = await client.models.generateContent({
        model: "gemini-3-flash-preview", // Gemini 3 Flash - latest model for ad copy
        contents: [{
          parts: [{ text: prompt }]
        }],
        generationConfig: {
          temperature: 0.5, // Balance between creativity and consistency
          maxOutputTokens: 3000,
          responseMimeType: "application/json"
        }
      });

      const responseText = response.text || response.candidates?.[0]?.content?.parts?.[0]?.text || '{}';
      const cleanedText = responseText.replace(/^```json\s*/gi, '').replace(/^```\s*/gi, '').replace(/```\s*$/gi, '').trim();
      const adCopy = JSON.parse(cleanedText);

      // STEP 1: Clean and validate with API v22
      if (adCopy.headlines) {
        adCopy.headlines = this.cleanAPIv22(adCopy.headlines);
      }
      if (adCopy.longHeadlines) {
        adCopy.longHeadlines = this.cleanAPIv22(adCopy.longHeadlines);
      }
      if (adCopy.descriptions) {
        adCopy.descriptions = this.cleanAPIv22(adCopy.descriptions);
      }

      // STEP 2: Enforce character limits (hard truncate if needed)
      if (adCopy.headlines) {
        adCopy.headlines = adCopy.headlines.map((h: string) => truncateHeadline(h));
      }
      if (adCopy.longHeadlines) {
        adCopy.longHeadlines = adCopy.longHeadlines.map((h: string) => truncateDescription(h));
      }
      if (adCopy.descriptions) {
        adCopy.descriptions = adCopy.descriptions.map((d: string) => truncateDescription(d));
      }

      // STEP 3: Verify minimum requirements for API v22
      const headlineCount = adCopy.headlines?.length || 0;
      const descriptionCount = adCopy.descriptions?.length || 0;
      
      if (headlineCount < 3) {
        console.warn(`⚠️ Only ${headlineCount} headlines generated, minimum 3 required for API v22`);
      }
      if (descriptionCount < 2) {
        console.warn(`⚠️ Only ${descriptionCount} descriptions generated, minimum 2 required for API v22`);
      }

      console.log(`✅ ${lang.languageName}: ${headlineCount} headlines, ${descriptionCount} descriptions (API v22 ✅)`);
      
      return adCopy;
      
    } catch (error) {
      console.error('❌ Gemini ad copy generation failed:', error);
      throw new Error(`Failed to generate ad copy with Gemini: ${(error as Error).message}`);
    }
  },

  /**
   * Build campaign from industry template
   * Uses template as AI system prompt for Google Ads API v22 compliant campaign creation
   */
  async buildCampaignFromTemplate(
    analysis: LandingPageAnalysis,
    template: IndustryTemplate,
    orgId: string,
    options?: {
      targetBudget?: number;
      isDemo?: boolean;
    }
  ): Promise<AutoCampaignResult> {
    console.log(`🎯 Building campaign from template: ${template.name}`);

    try {
      // STEP 1: Check template requirements
      console.log('✅ Step 1/6: Checking template requirements...');
      const requirementsCheck = await templateRequirementsChecker.checkTemplateRequirements(
        template,
        orgId
      );

      if (!requirementsCheck.canProceed) {
        throw new Error(
          `Missing requirements: ${requirementsCheck.missing.join(', ')}. ` +
          `Please complete setup: ${Object.values(requirementsCheck.instructions).join(' ')}`
        );
      }

      if (requirementsCheck.warnings.length > 0) {
        console.warn('⚠️ Template warnings:', requirementsCheck.warnings);
      }

      // STEP 2: Use template as AI system prompt
      console.log('🤖 Step 2/6: Generating campaign structure from template...');
      
      const aiSystemPrompt = `You are a Google Ads campaign expert. You MUST follow this industry-specific template EXACTLY:

${template.markdownContent}

CRITICAL REQUIREMENTS:
- ALL operations must use Google Ads API v22 (https://developers.google.com/google-ads/api/fields/v22/)
- You MUST create BOTH Search AND Performance Max campaigns (never just one)
- Use ONLY v22-supported field names and enum values
- Budget allocation: Search ${template.campaignConfig.searchBudgetPercent}%, PMax ${template.campaignConfig.pmaxBudgetPercent}%
- Search bidding: ${template.campaignConfig.searchBiddingStrategy}
- PMax bidding: ${template.campaignConfig.pmaxBiddingStrategy}
${template.campaignConfig.targetCpa ? `- Target CPA: €${template.campaignConfig.targetCpa}` : ''}
${template.campaignConfig.targetRoas ? `- Target ROAS: ${template.campaignConfig.targetRoas}` : ''}

Return campaign structure in JSON format with:
- searchCampaign: { name, keywords, adGroups, negativeKeywords }
- pmaxCampaign: { name, audienceSignals, assetGroups }
- explanation: why this structure works for this business`;

      const aiUserPrompt = `Create campaigns for this business:

**Business Information:**
- Name: ${analysis.businessName}
- Industry: ${analysis.industry}
- Website: ${analysis.url}
- Location: ${analysis.businessInfo?.city || 'Not specified'}

**Detected Conversions:**
${analysis.detectedConversions?.map(c => `- ${c.type}: ${c.name} (Value: €${c.value})`).join('\n') || 'None detected'}

**Budget:**
- Total Daily Budget: €${options?.targetBudget || template.recommendedDailyBudget.min}
- Search gets: €${Math.round(((options?.targetBudget || template.recommendedDailyBudget.min) * template.campaignConfig.searchBudgetPercent) / 100)}
- PMax gets: €${Math.round(((options?.targetBudget || template.recommendedDailyBudget.min) * template.campaignConfig.pmaxBudgetPercent) / 100)}

Generate BOTH campaigns following the template. Focus on ${analysis.primaryGoal || 'lead generation'}.`;

      const aiResponse = await openaiService.chat([
        { role: 'system', content: aiSystemPrompt },
        { role: 'user', content: aiUserPrompt }
      ]);

      // STEP 3: Parse AI response
      console.log('📋 Step 3/6: Parsing AI campaign structure...');
      let campaignStructure;
      try {
        // Extract JSON from markdown code blocks if present
        const jsonMatch = aiResponse.match(/```json\n([\s\S]*?)\n```/) || 
                         aiResponse.match(/```\n([\s\S]*?)\n```/) ||
                         [null, aiResponse];
        campaignStructure = JSON.parse(jsonMatch[1] || aiResponse);
      } catch (parseError) {
        console.error('Failed to parse AI response:', aiResponse);
        throw new Error('AI generated invalid campaign structure');
      }

      // STEP 4: Build keyword list with Gemini from scraped content (using template guidance)
      console.log('🔑 Step 4/6: Generating keywords with Gemini from scraped content...');
      console.log(`📋 Template: ${template.name} - Using template keyword patterns`);

      // Generate keywords with Gemini based on scraped content + template patterns
      const keywordIdeas = await keywordStrategyService.generateKeywordsWithGemini(analysis, 20);
      
      console.log(`✅ Generated ${keywordIdeas.length} keywords from template + scraped content`);

      // STEP 5: Generate ad copy with Gemini (Language-consistent, API v22 compliant)
      console.log('✍️ Step 5/6: Generating ad copy with Gemini...');
      const adCopy = await this.generateAdCopyWithGemini(analysis, 'search', { headlines: 15, descriptions: 4 });

      // STEP 6: Build final campaign draft
      console.log('🏗️ Step 6/6: Building final campaign draft...');
      
      const totalBudget = options?.targetBudget || template.recommendedDailyBudget.min;
      const searchBudget = Math.round((totalBudget * template.campaignConfig.searchBudgetPercent) / 100);
      const pmaxBudget = Math.round((totalBudget * template.campaignConfig.pmaxBudgetPercent) / 100);
      
      // Get language info
      const language = this.getConsistentLanguage(analysis);

      const draft: CampaignDraft = {
        orgId,
        createdBy: 'auto',
        campaignName: `${analysis.businessName || analysis.title} - Auto Search Campaign`,
        campaignType: 'search',
        landingPageUrl: analysis.url,
        analysis, // ✅ Store analysis for later use
        dailyBudget: searchBudget,
        locations: [analysis.businessInfo?.country || 'Germany'],
        languages: [language.languageName],
        startDate: new Date(),
        biddingStrategy: template.campaignConfig.searchBiddingStrategy as any,
        targetCpa: template.campaignConfig.targetCpa,
        targetRoas: template.campaignConfig.targetRoas,
        searchSettings: {
          adGroups: [],
          keywords: keywordIdeas.slice(0, 15).map(k => ({
            text: k.text,
            matchType: (k.recommendedMatchType?.toLowerCase() || 'phrase') as any
          })),
          negativeKeywords: campaignStructure.searchCampaign.negativeKeywords || [],
                headlines: adCopy.headlines || [],
                descriptions: adCopy.descriptions || [],
          displayUrlPath: '',
          conversionGoals: []
        },
        // PMax campaign configuration
        pmaxConfig: {
          name: `${analysis.businessName} - Performance Max`,
          dailyBudget: pmaxBudget,
          biddingStrategy: template.campaignConfig.pmaxBiddingStrategy,
          targetCpa: template.campaignConfig.targetCpa,
          targetRoas: template.campaignConfig.targetRoas,
          audienceSignals: campaignStructure.pmaxCampaign?.audienceSignals || [],
          assetGroups: campaignStructure.pmaxCampaign?.assetGroups || []
        }
      };

      // Track template usage
      await industryTemplateService.trackTemplateUsage(template.id);

      return {
        draft,
        explanation: campaignStructure.explanation || 
          `Campaign built using ${template.name} template with REAL scraped data. Search (${template.campaignConfig.searchBudgetPercent}%) + PMax (${template.campaignConfig.pmaxBudgetPercent}%) structure.`,
        aiDecisions: {
          keywords: `Generated ${keywordIdeas.length} REAL keywords from scraped content using Gemini + template patterns`,
          budget: `Total €${totalBudget}/day split: Search €${searchBudget}, PMax €${pmaxBudget}`,
          bidding: `Search: ${template.campaignConfig.searchBiddingStrategy}, PMax: ${template.campaignConfig.pmaxBiddingStrategy}`,
          adGroups: `Template-optimized structure with ${language.languageName} content`
        }
      };

    } catch (error) {
      console.error('Error building campaign from template:', error);
      throw error;
    }
  }
};

